import { Tearsheet } from '@carbon/ibm-products';
import { Button, InlineNotification, Modal } from '@carbon/react';
import { ChevronDown, ChevronUp } from '@carbon/react/icons';
import { SplitPanel } from 'components/split-panel';
import { useAtomValue, useSetAtom } from 'jotai';
import { isEqual, omitBy } from 'lodash-es';
import { useState } from 'react';
import { AssetForm } from './asset-form';
import { AssetMap } from './bulk-map';
import { addRowAtom, curationRowsAtom, updateRowAtom } from './atoms';
import styles from './bulk-upload.module.scss';
import { isBlank, presenceErrors } from './fields';
import { curationMapRow, mapRow, resolvedLocationId } from './map';
import type { FieldKey, RowValues } from './types';

/**
 * Which tearsheet is open: Add, Edit over a fixed list to walk, or Retry: a
 * submitted row edited before it is retried (its values handed in, saved
 * through its `onSave`, never into the curation rows).
 */
export type EditorState =
  | { mode: 'add' }
  | { mode: 'edit'; clientId: string; walk: string[] }
  | {
      mode: 'retry';
      values: RowValues;
      errors: Partial<Record<FieldKey, string>>;
      referenceLocationId: number | null;
      onSave: (values: RowValues) => void;
    };

/** What a new row starts with; the server applies the other defaults. */
const NEW_ROW_VALUES: RowValues = {
  gridException: false,
  titleQuality: 'none',
  titleRiskFactor: 'none',
};

const comparable = (values: RowValues) => omitBy(values, isBlank);

interface AssetTearsheetProps {
  editor: EditorState;
  onClose: () => void;
  onNavigate?: (clientId: string) => void;
}

/**
 * The Add asset / Edit asset tearsheet. Presence errors show at once; the
 * server's errors for the saved values show on each field until it is edited.
 * Edit walks `walk` (the rows filtered and searched when it opened) with
 * Previous and Next, asking to save or discard unsaved changes first. Mount it
 * keyed by the row so each row starts from its own values.
 * @param {AssetTearsheetProps} props - Editor state and handlers
 * @return {JSX.Element} The tearsheet
 */
export const AssetTearsheet = ({
  editor,
  onClose,
  onNavigate = () => {},
}: AssetTearsheetProps) => {
  const rows = useAtomValue(curationRowsAtom);
  const addRow = useSetAtom(addRowAtom);
  const updateRow = useSetAtom(updateRowAtom);
  const row =
    editor.mode === 'edit'
      ? rows.find((candidate) => candidate.clientId === editor.clientId)
      : undefined;
  const [baseline] = useState<RowValues>(
    editor.mode === 'retry' ? editor.values : (row?.values ?? NEW_ROW_VALUES)
  );
  // The survey area the server resolved for `baseline`, if any.
  const [baselineLocationId] = useState<number | null>(() => {
    if (editor.mode === 'retry') return editor.referenceLocationId;
    return row ? curationMapRow(row).referenceLocationId : null;
  });
  const [draft, setDraft] = useState<RowValues>(baseline);
  const [pendingTarget, setPendingTarget] = useState<string | null>(null);

  const isAdd = editor.mode === 'add';
  const isDirty = !isEqual(comparable(draft), comparable(baseline));
  const missing = presenceErrors(draft);
  const isComplete = Object.keys(missing).length === 0;

  const serverErrors = Object.fromEntries(
    Object.entries(
      (editor.mode === 'retry' ? editor.errors : row?.result?.errors) ?? {}
    ).filter(([key]) => {
      // A survey area error stands until either the name or the ID is changed.
      const fields = (
        key === 'surveyArea' || key === 'surveyAreaId'
          ? ['surveyArea', 'surveyAreaId']
          : [key]
      ) as FieldKey[];
      return fields.every((field) =>
        isEqual(draft[field] ?? null, baseline[field] ?? null)
      );
    })
  );
  const errors = { ...serverErrors, ...missing };

  const save = () => {
    if (editor.mode === 'retry') {
      editor.onSave(draft);
    } else if (editor.mode === 'edit') {
      updateRow({ clientId: editor.clientId, values: draft });
    } else {
      addRow(draft);
    }
  };

  const walk = editor.mode === 'edit' ? editor.walk : [];
  const index = editor.mode === 'edit' ? walk.indexOf(editor.clientId) : -1;
  const goTo = (target: string | undefined) => {
    if (!target) return;
    if (isDirty) {
      setPendingTarget(target);
    } else {
      onNavigate(target);
    }
  };

  return (
    <>
      <Tearsheet
        open
        className={styles.tearsheet}
        onClose={onClose}
        title={isAdd ? 'Add asset' : 'Edit asset'}
        hasCloseIcon
        closeIconDescription="Close"
        headerActions={
          editor.mode !== 'edit' ? undefined : (
            <div className={styles.tearsheetNav}>
              <Button
                kind="ghost"
                size="sm"
                hasIconOnly
                renderIcon={ChevronUp}
                iconDescription="Previous asset"
                tooltipPosition="bottom"
                disabled={index <= 0}
                onClick={() => goTo(walk[index - 1])}
              />
              <Button
                kind="ghost"
                size="sm"
                hasIconOnly
                renderIcon={ChevronDown}
                iconDescription="Next asset"
                tooltipPosition="bottom"
                disabled={index < 0 || index >= walk.length - 1}
                onClick={() => goTo(walk[index + 1])}
              />
            </div>
          )
        }
        actions={[
          {
            key: 'cancel',
            kind: 'secondary',
            label: 'Cancel',
            onClick: onClose,
          },
          {
            key: 'save',
            kind: 'primary',
            label: isAdd ? 'Add' : 'Save',
            disabled: !isComplete || (!isAdd && !isDirty),
            onClick: () => {
              save();
              onClose();
            },
          },
        ]}
      >
        <div className={styles.tearsheetBody}>
          <SplitPanel
            leftWidth="60%"
            left={
              <div className={styles.tearsheetForm}>
                {!isAdd && isDirty && (
                  <InlineNotification
                    kind="info"
                    lowContrast
                    hideCloseButton
                    title="Unsaved changes"
                  />
                )}
                <AssetForm
                  values={draft}
                  errors={errors}
                  referenceLocationId={baselineLocationId}
                  onChange={setDraft}
                />
              </div>
            }
            right={
              <div className={styles.tearsheetMap} data-testid="asset-map-slot">
                <AssetMap
                  row={mapRow(
                    'editing',
                    draft,
                    resolvedLocationId(draft, baseline, baselineLocationId)
                  )}
                />
              </div>
            }
          />
        </div>
      </Tearsheet>
      {pendingTarget !== null && (
        <Modal
          open
          modalHeading="Unsaved changes"
          primaryButtonText="Save"
          secondaryButtonText="Discard"
          primaryButtonDisabled={!isComplete}
          onRequestClose={() => setPendingTarget(null)}
          onSecondarySubmit={() => onNavigate(pendingTarget)}
          onRequestSubmit={() => {
            save();
            onNavigate(pendingTarget);
          }}
        >
          <p>Save your changes to this asset before moving on?</p>
        </Modal>
      )}
    </>
  );
};
